import { liteClient } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { connectClearRefinements, connectMenu, connectPagination } from 'instantsearch.js/es/connectors';
import {
	searchBox,
	configure,
	stats,
	pagination,
	panel,
	refinementList,
	menu,
	menuSelect,
	hits,
	currentRefinements,
	hierarchicalMenu,
} from 'instantsearch.js/es/widgets';
import { history } from 'instantsearch.js/es/lib/routers';
// console.log('test');
export default class StuurluiAlgolia {
	constructor(indexName, algoliaApiKey, algoliaAppId, options = {}) {
		this.client = liteClient(algoliaAppId, algoliaApiKey);
		this.slugToLabel = {};
		this.indexName = indexName;

		const instantsearchOptions = {
			indexName: this.indexName,
			searchClient: this.client,
			future: {
				preserveSharedStateOnUnmount: true,
			},
		};

		if (!options.disableRouting) {
			instantsearchOptions.routing = {
				router: history({
					createURL({ location, routeState, qsModule }) {
						const { origin, pathname } = location;
						const params = {};

						// Include all routeState keys except 'query' (handled separately)
						Object.keys(routeState).forEach((key) => {
							if (key !== 'query' && routeState[key] && routeState[key].length) {
								if (Array.isArray(routeState[key])) {
									params[key] = routeState[key].join(',');
								} else {
									params[key] = routeState[key];
								}
							}
						});

						params.q = routeState.query || undefined;

						const queryString = qsModule.stringify(params, {
							addQueryPrefix: true,
							arrayFormat: 'repeat',
						});

						return `${origin}${pathname}${queryString}`;
					},
					parseURL({ qsModule, location }) {
						const parsed = qsModule.parse(location.search.slice(1), {
							ignoreQueryPrefix: true,
							comma: false,
						});

						const result = {};

						// Include all parsed keys except 'q' (handled separately)
						Object.keys(parsed).forEach((key) => {
							if (key !== 'q' && parsed[key]) {
								result[key] = Array.isArray(parsed[key]) ? parsed[key] : parsed[key].split(',');
							}
						});

						// Add query field separately
						result.query = parsed.q || undefined;

						return result;
					},
				}),
				stateMapping: {
					stateToRoute(uiState) {
						const s = uiState[indexName] || {};
						let results = {
							query: s.query || '',
						};

						// Include all refinementList attributes
						if (s.refinementList) {
							Object.keys(s.refinementList).forEach((key) => {
								if (s.refinementList[key] && s.refinementList[key].length > 0) {
									results[key] = s.refinementList[key];
								}
							});
						}

						return results;
					},

					routeToState(routeState) {
						let results = {
							hierarchicalMenu: {},
							refinementList: {},
							query: routeState.query || '',
						};

						// Include all routeState keys except 'query' as refinementList attributes
						Object.keys(routeState).forEach((key) => {
							if (key !== 'query' && routeState[key]) {
								results.refinementList[key] = Array.isArray(routeState[key])
									? routeState[key]
									: routeState[key].split(',');
							}
						});

						return {
							[indexName]: results,
						};
					},
				},
			};
		}

		this.search = instantsearch(instantsearchOptions);
		this.facetTemplates = {
			hierarchical: {
				item(item) {
					const { label, cssClasses, url, count, isRefined } = item;

					return `
					<a class="${cssClasses.link}" href="${url}" target="_self">
						<span class="size-4 mr-1 border-2 ${isRefined ? ' bg-primary border-primary justify-center items-center inline-flex -translate-y-px' : ' border-black inline-block translate-y-1'}"><i class="fa-solid fa-check text-white text-[10px] !w-auto${isRefined ? '' : ' !hidden'}"></i></span>
						<span class="${cssClasses.label}">${label}
							<span class="${cssClasses.count}"> (${count.toLocaleString()})</span>
						</span>
					</a>
					`;
				},
				showMoreText: ({ isShowingMore }) =>
					isShowingMore
						? 'Toon minder <i class="fa-solid fa-chevron-up"></i>'
						: 'Toon meer <i class="fa-solid fa-chevron-down"></i>',
			},
			checkbox: {
				item(item) {
					const { label, value, isRefined, disabled, count } = item;

					return `
					<label class="group cursor-pointer relative hocus:underline focus-visible:outline-style! ${isRefined ? 'is-checked' : ''}" tabindex="0">
						<input type="checkbox" class="absolute -left-[999em]" value="${item.value}" ${isRefined ? 'checked' : ''} tabindex="-1">
						<span class="cursor-pointer relative p-0 mr-2 size-4 border inline-flex! justify-center items-center border-black bg-white group-[&.is-checked]:bg-tertiary group-[&.is-checked]:border-tertiary">
							<i class="fa-solid fa-check hidden text-white text-[8px] group-[&.is-checked]:block"></i>
						</span><span class="text-black inline-block translate-y-1">${label} (${count})</span>
					</label>
				`;
				},
				showMoreText: ({ isShowingMore }) =>
					isShowingMore
						? 'Toon minder <i class="fa-solid fa-chevron-up"></i>'
						: 'Toon meer <i class="fa-solid fa-chevron-down"></i>',
			},
			radio: {
				item(item) {
					const { label, value, isRefined } = item;
					return `
						<label class="${isRefined ? 'is-checked' : ''}">
							<input type="radio" name="refinement" value="${value}" ${isRefined ? 'checked' : ''} tabindex="-1">
							<span>${label}</span>
						</label>
					`;
				},
				showMoreText: ({ isShowingMore }) => (isShowingMore ? 'Toon minder' : 'Toon meer'),
			},
			dropdown: {
				item(item) {
					const { label, isRefined } = item;
					return `<option value="${label}" ${isRefined ? 'selected' : ''}>${label}</option>`;
				},
			},
		};
	}

	setConfig(config) {
		return configure(config);
	}

	addWidgets(widgets) {
		this.search.addWidgets(widgets);
	}

	statsWidget(container = '#stats') {
		return stats({
			container,
			templates: {
				text(data, { html }) {
					let count = '';
					if (data.hasManyResults) {
						count += `${data.nbHits} ${strl_vars.more_results}`;
						return html`<span>${count}</span>`;
					} else if (data.hasOneResult) {
						count += `1 ${strl_vars.one_result}`;
						return html`<span>${count}</span>`;
					} else {
						return '';
					}
				},
			},
			cssClasses: {
				text: 'text-secondary-140',
			},
		});
	}

	recentlySeenWidget(container = '#recently-viewed') {
		return {
			init() {
				const storedItems = localStorage.getItem('recentlySeen');
				this.items = storedItems ? JSON.parse(storedItems) : [];

				if (this.items.length > 0) {
					this.items = this.items.slice(-3);
				}

				this.render(container);
			},
			render() {
				const containerElement = typeof container === 'string' ? document.querySelector(container) : container;
				containerElement.innerHTML =
					`<h3>Recent bekeken</h3>` +
					this.items
						.map(
							(item) => `
							<a class="recent-item" href="${item.link}" target="_self">
								<h4 class="">${item.title}</h4>
								<span class="sr-only">
									${strl_vars.more_info} ${item.title}
								</span>
							</a>
						`
						)
						.join('');
			},
		};
	}

	overviewSearchBoxWidget(container, submitLabel = 'Search') {
		return this.searchBoxWidget(container, {
			autofocus: false,
			showLoadingIndicator: false,
			cssClasses: {
				form: 'ais-SearchBox-form',
				input: 'ais-SearchBox-input',
				submit: 'ais-SearchBox-submit submit-btn max-lg:btn max-lg:square max-lg:justify-center',
				submitIcon: 'hidden',
				loadingIndicator: 'hidden',
				loadingIcon: 'hidden',
			},
			templates: {
				submit(_item, { html }) {
					const icon =
						typeof strl_vars !== 'undefined' && strl_vars.icons?.magnifying_glass
							? strl_vars.icons.magnifying_glass
							: '';
					return html`<span
							class="inline-flex items-center"
							aria-hidden="true"
							dangerouslySetInnerHTML=${{ __html: icon }}
						></span
						><span class="max-lg:hidden">${submitLabel}</span>`;
				},
			},
		});
	}

	searchBoxWidget(container = '#searchbox', widgetOptions = {}) {
		const searchRoot = document.querySelector(container);
		const placeholder = searchRoot?.dataset?.placeholder;

		const defaultCssClasses = {
			form: 'relative',
			input: 'h-16! pr-2! w-full [&:placeholder-shown+.submit-btn]:flex',
			submit: 'submit-btn p-0! flex-center aspect-square h-[calc(100%-1rem)] absolute top-1/2 right-2 -translate-y-1/2 hidden',
			submitIcon: 'size-4 fill-secondary',
		};

		const cssClasses = {
			...defaultCssClasses,
			...(widgetOptions.cssClasses || {}),
		};

		const config = {
			container,
			placeholder: placeholder || strl_vars.search_label,
			showReset: false,
			showSubmit: true,
			showLoadingIndicator:
				widgetOptions.showLoadingIndicator !== undefined ? widgetOptions.showLoadingIndicator : true,
			autofocus: widgetOptions.autofocus !== false,
			cssClasses,
		};

		if (widgetOptions.templates) {
			config.templates = widgetOptions.templates;
		}

		return searchBox(config);
	}

	clearRefinementsWidget(container = '#clear-refinements', cssClasses = {}) {
		return connectClearRefinements((renderOptions, isFirstRendering) => {
			const { refine, canRefine, instantSearchInstance } = renderOptions;
			const containerElement = typeof container === 'string' ? document.querySelector(container) : container;

			if (isFirstRendering) {
				containerElement.innerHTML = `<button id="clear-refinements-btn" class="${cssClasses.button}">
					${strl_vars.reset_filters_label}
				</button>`;
			}

			const button = containerElement.querySelector('#clear-refinements-btn');
			button.disabled = !canRefine;

			button.onclick = () => {
				refine();
				instantSearchInstance.helper
					.setQueryParameter('aroundLatLng', undefined)
					.setQueryParameter('aroundRadius', undefined)
					.search();
			};
		})({ container, cssClasses });
	}

	customPaginationWidget(container = '#search-pagination', widgetOptions = {}) {
		const defaultCssClasses = {
			list: 'pagination-list',
			listMobile: 'mobile lg:hidden',
			listDesktop: 'desktop hidden lg:flex',
			link: 'no-underline',
			linkActive: '',
			ellipsisSpan: '',
			prevNextLink: '',
			activeItem: 'active-item',
			totalPages: 'total-pages flex justify-center items-center text-secondary-140 font-bold',
		};
		const cssClasses = {
			...defaultCssClasses,
			...(widgetOptions.cssClasses || {}),
		};

		const paginationLinkClass = (isActive, isPrevNext, isDisabled) => {
			let classes =
				isActive && cssClasses.linkActive ? cssClasses.linkActive : cssClasses.link;
			if (isPrevNext && cssClasses.prevNextLink && !(isActive && cssClasses.linkActive)) {
				classes += ` ${cssClasses.prevNextLink}`;
			}
			if (isDisabled) {
				classes += ' pointer-events-none opacity-40';
			}
			return classes;
		};

		return connectPagination((renderOptions) => {
			const { currentRefinement, nbPages, isFirstPage, isLastPage, refine } = renderOptions;
			const containerElement = typeof container === 'string' ? document.querySelector(container) : container;
			const totalPages = 3;

			// Only render the pagination if there is more than one page
			if (nbPages <= 1) {
				containerElement.innerHTML = '';
				return;
			}

			containerElement.innerHTML = `
			<ul class="${cssClasses.list} ${cssClasses.listMobile}">
				<li class="pagination-item prev-page">
					<a class="${paginationLinkClass(false, true, isFirstPage)}" data-value="${currentRefinement - 1}">
					<i class="fas fa-arrow-left"></i>
					</a>
				</li>
				<li class="${cssClasses.totalPages}">
					<span>${currentRefinement + 1} / ${nbPages}</span>
				</li>
				<li class="pagination-item next-page">
					<a class="${paginationLinkClass(false, true, isLastPage)}" data-value="${currentRefinement + 1}">
					<i class="fas fa-arrow-right"></i>
					</a>
				</li>
			</ul>
			<ul class="${cssClasses.list} ${cssClasses.listDesktop}">
				${!isFirstPage
					? `
							<li class="pagination-item prev-page">
								<a class="${paginationLinkClass(false, true, false)}" data-value="${currentRefinement - 1}">
								<i class="fas fa-arrow-left"></i>
								</a>
							</li>
							`
					: ''
				}
				${this.getVisiblePages(currentRefinement, nbPages, totalPages)
					.map((page) => {
						if (page === '...') {
							return `
								<li class="pagination-item ellipsis">
									<span class="${cssClasses.ellipsisSpan}">...</span>
								</li>
							`;
						}
						const isActive = currentRefinement === page;
						return `
							<li class="pagination-item ${isActive ? cssClasses.activeItem : ''}">
								<a class="${paginationLinkClass(isActive, false, false)}" data-value="${page}">
								${page + 1}
								</a>
							</li>
						`;
					})
					.join('')}
					${!isLastPage
					? `
								<li class="pagination-item next-page">
									<a class="${paginationLinkClass(false, true, false)}" data-value="${currentRefinement + 1}">
									<i class="fas fa-arrow-right"></i>
									</a>
								</li>
								`
					: ''
				}
			</ul>
		`;

			[...containerElement.querySelectorAll('a')].forEach((element) => {
				element.addEventListener('click', (e) => {
					e.preventDefault();
					refine(e.currentTarget.dataset.value);
				});
			});
		})({ container });
	}

	paginationWidget(
		container = '#search-pagination',
		next = `<span class="pagination-arrow">${strl_vars.next_button} &raquo;</span>`,
		previous = `<span class="pagination-arrow">&laquo; ${strl_vars.prev_button}</span>`
	) {
		return pagination({
			container,
			showFirst: false,
			showLast: false,
			templates: {
				next,
				previous,
			},
			cssClasses: {
				list: 'pagination-list',
				selectedItem: 'active-item',
				item: 'pagination-item',
				disabledItem: 'pagination-disabled hidden',
			},
		});
	}

	paginationInfoWidget(container = '#pagination-info') {
		return connectPagination((renderOptions, isFirstRender) => {
			const { currentRefinement, nbPages, widgetParams } = renderOptions;
			const containerElement = typeof container === 'string' ? document.querySelector(container) : container;

			containerElement.innerHTML = '';

			const infoElement = document.createElement('span');
			infoElement.textContent = `${strl_vars.translate_page} ${currentRefinement + 1} ${strl_vars.of} ${nbPages}.`;

			containerElement.appendChild(infoElement);
		})({ container });
	}

	currentRefinementWidget(container = '#current-refinements', cssClasses = {}, terms = []) {
		return currentRefinements({
			container,
			cssClasses,
			transformItems(items) {
				const filteredOn = document.getElementById('filtered-on');
				if (items.length > 0) {
					items.forEach((item) => {
						item.refinements.forEach((refinement, index) => {
							if (item.label === 'post_type') {
								item.refinements[index].label = strl_vars.cpt_labels[item.refinements[index].label];
								return;
							}

							const parentAttr = refinement.attribute.replace('_slug', '');
							const correspondingTerm = terms[parentAttr]?.find(
								(term) => term.value === refinement.value
							);
							if (correspondingTerm && correspondingTerm.label) {
								item.refinements[index].label = correspondingTerm.label;
							}
						});
					});
					filteredOn?.classList.remove('hidden');
				} else {
					filteredOn?.classList.add('hidden');
				}
				return items;
			},
		});
	}

	async init(facets = []) {
		// Fetch facets for slug attributes to get all possible slugs
		const slugFacets = facets.map((attr) => `${attr}_slug`);
		const facetParams = {
			hitsPerPage: 0,
			maxValuesPerFacet: 500,
			facets: slugFacets,
		};

		// Fetch records to get taxonomy mappings from the tax_* arrays
		const recordParams = {
			hitsPerPage: 1000, // Get enough records to collect all taxonomy terms
			attributesToRetrieve: facets, // Only fetch the taxonomy attributes we need
		};

		const [facetResponse, recordResponse] = await Promise.all([
			this.client.search(
				[
					{
						indexName: this.indexName,
						query: '',
						params: facetParams,
					},
				],
				{ enableRules: false }
			),
			this.client.search(
				[
					{
						indexName: this.indexName,
						query: '',
						params: recordParams,
					},
				],
				{ enableRules: false }
			),
		]);

		if (facetResponse.results.length > 0 && recordResponse.results.length > 0) {
			const rawFacets = facetResponse.results[0]?.facets;
			const hits = recordResponse.results[0]?.hits || [];

			facets.forEach((attr) => {
				const slugAttribute = `${attr}_slug`;
				const slugFacet = rawFacets[slugAttribute] || {};
				const allSlugs = Object.keys(slugFacet);

				// Collect slug-to-name mappings from records
				const slugToNameMap = new Map();

				hits.forEach((hit) => {
					const taxonomyData = hit[attr];
					if (Array.isArray(taxonomyData)) {
						taxonomyData.forEach((term) => {
							if (term && term.slug && term.name) {
								slugToNameMap.set(term.slug, term.name);
							}
						});
					}
				});

				// Create mapping for all slugs found in facets
				this.slugToLabel[attr] = allSlugs.map((slug) => ({
					value: slug,
					label: slugToNameMap.get(slug) || slug, // Fallback to slug if name not found
				}));
			});
		}
	}

	getTerms() {
		return this.slugToLabel;
	}

	getVisiblePages(currentRefinement, nbPages, totalPages) {
		const pages = [];
		const leftEllipsis = '...';
		const rightEllipsis = '...';

		// If there are fewer pages than the max visible pages, show all pages
		if (nbPages <= totalPages) {
			for (let i = 0; i < nbPages; i++) {
				pages.push(i);
			}
			return pages;
		}

		// Always show the first page
		pages.push(0);

		// Calculate
		let startPage = Math.max(currentRefinement - Math.floor(totalPages / 2), 1);
		let endPage = Math.min(currentRefinement + Math.floor(totalPages / 2), nbPages - 1);

		if (startPage === 1) endPage = Math.min(totalPages - 1, nbPages - 1);
		if (endPage === nbPages - 1) startPage = Math.max(nbPages - totalPages, 1);

		for (let i = startPage; i <= endPage; i++) {
			pages.push(i);
		}

		if (pages[1] > 1) pages.splice(1, 0, leftEllipsis);
		if (pages[pages.length - 2] < nbPages - 2) pages.push(rightEllipsis);

		// Always show the last page
		if (pages[pages.length - 1] !== nbPages - 1) pages.push(nbPages - 1);

		return pages;
	}

	refinementPanelWidget(
		headerText,
		refinementOptions,
		hideIfEmpty = true,
		showMore = true,
		limit = 7,
		showMoreLimit = 99
	) {
		const facetType = refinementOptions.facetType || 'checkbox',
			defaultTemplates = this.facetTemplates[facetType],
			defaultRefinementOptions = {
				showMore,
				limit: limit,
				facetOrdering: true,
				templates: defaultTemplates,
				showMoreLimit: showMoreLimit,
			};

		const options = {
			...defaultRefinementOptions,
			...refinementOptions,
			transformItems: (items) => {
				if (!refinementOptions.hasOwnProperty('terms')) {
					return items;
				}

				const allTerms = refinementOptions.terms || [];

				const labelMap = {};

				allTerms.forEach(({ value, label }) => {
					labelMap[value] = label;
				});

				const updatedItems = items.map((item) => ({
					...item,
					label: labelMap[item.value] || item.label,
					disabled: false,
				}));

				const itemValues = new Set(items.map((i) => i.value));
				const missingItems = allTerms
					.filter((term) => !itemValues.has(term.value))
					.map((term) => ({
						value: term.value,
						label: term.label,
						count: 0,
						isRefined: false,
						disabled: true,
					}));

				const combined = [...updatedItems, ...missingItems];

				if (facetType === 'radio') {
					return combined;
				}

				const refinedItems = combined.filter((i) => i.isRefined);
				const enabledItems = combined.filter((i) => !i.isRefined && !i.disabled);
				const disabledItems = combined.filter((i) => !i.isRefined && i.disabled);

				return [...refinedItems, ...enabledItems, ...disabledItems];
			},
		},
			commonPanel = panel({
				hidden: (opts) => opts.items.length === 0 && hideIfEmpty,
				templates: { header: headerText },
				cssClasses: {
					root:
						facetType === 'radio'
							? 'radio-refinement'
							: facetType === 'dropdown'
								? 'dropdown-refinement'
								: facetType === 'hierarchical'
									? 'hierarchical-refinement'
									: 'checkbox-refinement',
				},
			});

		if (facetType === 'hierarchical') {
			return commonPanel(hierarchicalMenu)(options);
		}
		if (facetType === 'radio') {
			return commonPanel(menu)(options);
		}
		if (facetType === 'dropdown') {
			return commonPanel(menuSelect)(options);
		}
		return commonPanel(refinementList)(options);
	}

	hitsWidget(templates, container = '#hits', cssClasses = {}) {
		return hits({
			container,
			templates,
			cssClasses,
			transformItems(items) {
				return items.map((item) => {
					const originalHighlight = item._highlightResult || {};
					const titleHighlight = originalHighlight.title
						? {
							...originalHighlight.title,
							value: originalHighlight.title.value.replace(/&amp;/g, '&'),
						}
						: undefined;

					const newHighlightResult = {
						...originalHighlight,
						...(titleHighlight && { title: titleHighlight }),
					};
					return {
						...item,
						_highlightResult: newHighlightResult,
					};
				});
			},
		});
	}

	start() {
		this.search.start();
	}

	wcagFixes() {
		document.querySelectorAll('.ais-RefinementList').forEach((list) => {
			const showMoreBtn = list.querySelector('.ais-RefinementList-showMore');
			if (showMoreBtn) {
				showMoreBtn.addEventListener('click', (e) => {
					list.querySelectorAll('.ais-RefinementList-item input').forEach((item) => {
						item.setAttribute('tabindex', '0');
					});
				});
			}
		});

		const searchBox = document.getElementById('searchbox');
		const searchInput = searchBox.querySelector('form input');

		if (searchBox && searchInput) {
			const searchId = searchBox?.dataset?.searchId;
			const placeholder = searchBox?.dataset?.placeholder;
			const searchLabel = document.createElement('label');
			searchInput.id = searchId;
			searchInput.setAttribute('aria-label', searchInput.getAttribute('placeholder'));
			searchLabel.innerHTML = placeholder;
			searchLabel.setAttribute('for', searchId);
			searchLabel.classList.add('hidden');
			searchBox.querySelector('form')?.prepend(searchLabel);
			searchBox.querySelector('.ais-SearchBox-loadingIcon')?.removeAttribute('aria-label');
		}
	}
}

export function bindOverviewSearchboxWcag(containerId) {
	const searchBox = document.getElementById(containerId);
	if (!searchBox) {
		return;
	}
	const searchInput = searchBox.querySelector('form input');
	if (!searchInput) {
		return;
	}
	if (searchInput.getAttribute('data-strl-wcag') === '1') {
		return;
	}
	searchInput.setAttribute('data-strl-wcag', '1');
	const searchId = searchBox.dataset.searchId;
	const placeholder = searchBox.dataset.placeholder;
	if (searchId) {
		searchInput.id = searchId;
	}
	const searchLabel = document.createElement('label');
	searchLabel.textContent = placeholder || '';
	searchLabel.setAttribute('for', searchInput.id);
	searchLabel.classList.add('sr-only');
	const form = searchBox.querySelector('form');
	if (form) {
		form.prepend(searchLabel);
	}
	searchInput.setAttribute('aria-label', searchInput.getAttribute('placeholder') || '');
	const loadingParts = searchBox.querySelectorAll(
		'.ais-SearchBox-loadingIndicator, .ais-SearchBox-loadingIcon'
	);
	for (let li = 0; li < loadingParts.length; li++) {
		loadingParts[li].classList.add('hidden');
		loadingParts[li].setAttribute('aria-hidden', 'true');
	}
	const defaultSubmitIcons = searchBox.querySelectorAll('.ais-SearchBox-submitIcon');
	for (let si = 0; si < defaultSubmitIcons.length; si++) {
		defaultSubmitIcons[si].classList.add('hidden');
		defaultSubmitIcons[si].setAttribute('aria-hidden', 'true');
	}

	const outerSubmit = searchBox.querySelector('.ais-SearchBox-submit');
	if (outerSubmit) {
		const nestedSubmit = outerSubmit.querySelector('button');
		if (nestedSubmit && nestedSubmit !== outerSubmit) {
			while (nestedSubmit.firstChild) {
				outerSubmit.appendChild(nestedSubmit.firstChild);
			}
			nestedSubmit.remove();
		}
	}
}
